import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { FixedRoomGeometry } from "../hooks/useCanvasLogic";

interface FixedRoomDetailsDialogProps {
    open: boolean;
    draft: FixedRoomGeometry | null;
    defaultName: string;
    onCancel: () => void;
    onSave: (details: { name: string; maxExpansion: number }) => void;
}

export const FixedRoomDetailsDialog: React.FC<FixedRoomDetailsDialogProps> = ({
    open,
    draft,
    defaultName,
    onCancel,
    onSave,
}) => {
    const [name, setName] = useState(defaultName);

    useEffect(() => {
        if (!open) return;
        setName(defaultName);
    }, [open, defaultName]);

    const summary = useMemo(() => {
        if (!draft) return "";
        return `x: ${draft.x}, y: ${draft.y}, width: ${draft.width}, height: ${draft.height}`;
    }, [draft]);

    const handleSave = () => {
        const trimmed = name.trim();
        if (!trimmed) return;
        onSave({
            name: trimmed,
            maxExpansion: 0,
        });
    };

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontFamily: "Poppins", fontWeight: 700 }}>
                Fixed Room Details
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontFamily: "Poppins", fontSize: 12, color: "#5a5a5a", mb: 1.5 }}>
                    Rectangle captured from canvas. Add details before saving.
                </Typography>
                {summary ? (
                    <Box
                        sx={{
                            mb: 2,
                            px: 1.5,
                            py: 1,
                            borderRadius: "8px",
                            border: "1px solid #d8d8d8",
                            bgcolor: "#fafafa",
                            fontFamily: "Poppins",
                            fontSize: 12,
                        }}
                    >
                        {summary}
                    </Box>
                ) : null}

                <TextField
                    label="Room Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    autoFocus
                    sx={{ mb: 2 }}
                    inputProps={{ maxLength: 60 }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onCancel} sx={{ textTransform: "none" }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!name.trim()}
                    sx={{
                        textTransform: "none",
                        bgcolor: "#FF9A56",
                        '&:hover': { bgcolor: '#153a63' },
                    }}
                >
                    Save Fixed Room
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FixedRoomDetailsDialog;
